import React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ToolSection, ToolsSidebarProps } from '../About/types';
import { toolsSections } from '../About/helpers';
import { userAppContext } from '@/context/Context';
import { ChevronRightIcon } from '@heroicons/react/24/solid';

const ToolsSidebar: React.FC<
  ToolsSidebarProps & { collapsed?: boolean; variant?: 'default' | 'profile' }
> = ({ onClick, collapsed = false, variant = 'default' }) => {
  const [searchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');

  const { user } = userAppContext();
  const userRoles = user?.['cognito:groups'] ?? [];

  const isActiveSection = (section: string) => sectionParam === section;

  const visibleSections = toolsSections.filter(section => {
    if (!section.roles) return true;
    return section.roles.some(role => userRoles.includes(role));
  });

  const groupedSections = visibleSections.reduce(
    (acc, section) => {
      if (!acc[section.group]) acc[section.group] = [];
      acc[section.group].push(section);
      return acc;
    },
    {} as Record<string, ToolSection[]>,
  );

  if (variant === 'profile') {
    return (
      <>
        {Object.entries(groupedSections).map(([group, items]) => (
          <React.Fragment key={group}>
            <p className="text-[10px] font-semibold tracking-widest uppercase text-blue px-3 pt-3 pb-1">
              {group}
            </p>

            {items.map(({ label, value, icon, children }) => {
              const isActive =
                isActiveSection(value) || children?.some(child => isActiveSection(child.value));

              return (
                <React.Fragment key={value}>
                  <Link
                    to={`?section=${value}`}
                    className={`mb-1 dropdown-item-profile text-wrap ${isActive ? 'active' : ''}`}
                    onClick={onClick}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2">
                        {label}
                        {icon === 'aida' && (
                          <img src="/static/images/eyesAida.svg" alt="AIDA" width="18" height="18" />
                        )}
                      </span>
                      <ChevronRightIcon className="w-4 h-4 flex-shrink-0" />
                    </div>
                  </Link>

                  {children?.map(child => (
                    <Link
                      key={child.value}
                      to={`?section=${child.value}`}
                      className={`mb-1 dropdown-item-profile text-wrap pl-6 ${
                        isActiveSection(child.value) ? 'active' : ''
                      }`}
                      onClick={onClick}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span>{child.label}</span>
                        <ChevronRightIcon className="w-3 h-3 flex-shrink-0" />
                      </div>
                    </Link>
                  ))}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        ))}
      </>
    );
  }

  return (
    <div
      className={`w-full ${collapsed ? 'max-w-[64px]' : 'max-w-[320px]'} lg:mb-6 transition-all duration-300`}
    >
      <div className="p-3">
        {Object.entries(groupedSections).map(([group, items]) => (
          <div key={group} className="mt-3">
            {!collapsed && (
              <div className="uppercase font-bold text-lg mb-2 tracking-wider text-blue">{group}</div>
            )}

            <div className="flex flex-col gap-1">
              {items.map(({ label, value, icon, children }) => {
                const isActive =
                  isActiveSection(value) || children?.some(child => isActiveSection(child.value));

                return (
                  <div key={value}>
                    <Link
                      to={`?section=${value}`}
                      className={`sidebar-link ${isActive ? 'active' : ''}`}
                      onClick={onClick}
                    >
                      {!collapsed && (
                        <span className="flex items-center gap-2">
                          {label}
                          {icon === 'aida' && (
                            <img src="/static/images/eyesAida.svg" alt="bot-aida" width="20" height="20" />
                          )}
                        </span>
                      )}
                    </Link>

                    {children && !collapsed && (
                      <div className="ml-4 mt-1 flex flex-col gap-1">
                        {children.map(child => (
                          <Link
                            key={child.value}
                            to={`?section=${child.value}`}
                            className={`sidebar-link ${isActiveSection(child.value) ? 'active' : ''}`}
                            onClick={onClick}
                          >
                            <span>{child.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ToolsSidebar;
